import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { HistoryService } from "../../services/history.service.js";
import { PaginationControls } from "../../components/common/PaginationControls.js";
import { modal } from "../../components/modal.js";

const db = getFirestore(app);

export const HistoryTab = async (target, user) => {
    let freshSeriesMap = new Map();

    const renderEmptyState = (target, title, subtitle, action) => {
        target.innerHTML = `
            <div class="flex flex-col items-center justify-center py-20 text-center">
                <div class="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                    <span class="material-symbols-outlined text-4xl">inventory_2</span>
                </div>
                <h3 class="text-gray-900 font-bold mb-1">${title}</h3>
                <p class="text-gray-500 text-sm mb-6">${subtitle}</p>
                ${action ? `<button id="btn-empty-action" class="px-5 py-2 bg-indigo-50 text-indigo-600 font-bold rounded-full text-sm hover:bg-indigo-100 transition-colors">${window.locale.t('history.empty.action_btn')}</button>` : ''}
            </div>
        `;
        if (action) {
            target.querySelector('#btn-empty-action').onclick = action;
        }
    };

    let historyData = [];

    const loadFreshSeriesData = async (historyItems) => {
        if (!historyItems || historyItems.length === 0) return;

        try {
            const missingIds = historyItems
                .filter(item => item.seriesId && !freshSeriesMap.has(item.seriesId))
                .map(item => item.seriesId);

            const uniqueMissingIds = [...new Set(missingIds)];
            if (uniqueMissingIds.length === 0) return;

            const missingDocs = await Promise.all(
                uniqueMissingIds.map(async (id) => {
                    try {
                        return await getDoc(doc(db, "series", id));
                    } catch (e) {
                        // Permission/Existence error -> Return null to avoid crashing entire batch
                        // [LOG-SUPRESSED] This is expected behavior for unpublished/deleted works.
                        // console.debug(`[HistoryTab] Skip restricted/missing series: ${id}`, e.code);
                        return null;
                    }
                })
            );

            missingDocs.forEach(d => {
                if (d && d.exists()) {
                    freshSeriesMap.set(d.id, d.data());
                }
            });

            // [FIX] Filter out deleted or missing series
            const validItems = historyItems.filter(item => {
                const fresh = freshSeriesMap.get(item.seriesId);
                // Keep if:
                // 1. Fresh data exists AND status is NOT deleted
                // 2. OR if we failed to fetch fresh data (edge case), conservatively maybe hide? 
                // Decision: User wants to HIDE. So if no fresh data -> HIDE.
                if (!fresh) return false;
                if (fresh.status === 'deleted') return false;
                if (!fresh.title) return false; // [FIX] Filter out missing titles
                return true; // [FIX] Restore missing return statement
            });

            // Mutate the original array references if possible or return new
            // Since `historyData` is a let variable in the outer scope, we should update it.
            // But `loadFreshSeriesData` is called with `historyData`.
            // We need to return the valid items or update the caller.
            // Let's return the valid items and update the caller.
            return validItems;

        } catch (e) {
            console.error("Failed to load series data", e);
            return historyItems; // Return original on error to be safe? Or empty? Safe.
        }
    };

    // Internal State
    let currentPage = 1;

    const updateView = (page) => {
        if (!historyData || historyData.length === 0) return; // Should be handled by initial check

        const itemsPerPage = 10;
        const totalItems = historyData.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);

        if (page < 1) page = 1;
        if (page > totalPages) page = totalPages;
        currentPage = page;


        const startIdx = (currentPage - 1) * itemsPerPage;
        const pageItems = historyData.slice(startIdx, startIdx + itemsPerPage);

        let html = '<div class="divide-y divide-gray-100 mb-6">'; // mb-6 for pagination space
        pageItems.forEach(item => {
            const displayOrder = !isNaN(parseInt(item.lastEpisodeOrder)) ? parseInt(item.lastEpisodeOrder) + 1 : item.lastEpisodeOrder;

            const freshSeries = freshSeriesMap.get(item.seriesId);
            let finalTitle = item.seriesTitle;
            let finalThumb = item.thumbnail;

            if (freshSeries) {
                finalTitle = freshSeries.title;
                finalThumb = freshSeries.coverImage || freshSeries.thumbnail || finalThumb;
            }

            finalThumb = finalThumb || 'https://placehold.co/100x100?text=No+Img';

            if (finalThumb && (finalThumb.includes('pstatic.net') || finalThumb.includes('naver.com') || finalThumb.includes('daumcdn.net'))) {
                finalThumb = "https://wsrv.nl/?url=" + encodeURIComponent(finalThumb) + "&w=200&output=webp";
            }

            const formatDate = (val) => {
                if (!val) return '';
                try {
                    const date = val.toDate ? val.toDate() : new Date(val);
                    // [I18n] Use window.locale.current if available, else default
                    const locale = window.locale?.current || 'ko-KR';
                    return isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale);
                } catch (e) { return ''; }
            };

            // [I18n] Localize Episode String
            const epString = window.locale.t('history.episode_fmt').replace('{n}', displayOrder);

            html += `
                        <div class="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer group relative pointer-events-auto" role="button" onclick="window.router.navigate('/series/${item.seriesId}/episode/${item.lastEpisodeId}'); event.stopPropagation();">
                            <div class="w-16 h-20 bg-gray-200 rounded-md overflow-hidden flex-shrink-0 shadow-sm relative">
                                <img src="${finalThumb}" class="w-full h-full object-cover" style="-webkit-touch-callout: none;">
                                <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                            </div>
                            <div class="flex-1 min-w-0">
                                <h4 class="font-bold text-gray-900 truncate">${finalTitle || window.locale.t('history.common.no_title')}</h4>
                                <p class="text-xs text-gray-500 mt-1">${window.locale.t('history.last_viewed')} <span class="text-indigo-600 font-bold">${epString}</span></p>
                                <p class="text-[10px] text-gray-400 mt-1">${formatDate(item.lastReadAt)}</p>
                            </div>
                            <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors rounded-full hover:bg-red-50 z-10 pointer-events-auto" onclick="event.stopPropagation(); window.MyPageActions_${target.id}.deleteHistory('${item.seriesId}');">
                                <span class="material-symbols-outlined text-lg">close</span>
                            </button>
                        </div>
                    `;
        });
        html += '</div>';

        if (totalPages > 1) {
            html += `<div id="history-pagination-container"></div>`;
        }

        target.innerHTML = html;

        if (totalPages > 1) {
            PaginationControls.render({
                target: target.querySelector('#history-pagination-container'),
                currentPage: currentPage,
                totalPages: totalPages,
                onPageChange: (newPage) => window[`MyPageActions_${target.id}`].setPage(newPage)
            });
        }
    };


    const init = async () => {
        // Fetch 100 items from Subcollection
        const { items } = await HistoryService.getHistory(user.uid, 100);
        historyData = items || [];

        // Ensure we have fresh series data
        if (freshSeriesMap.size === 0) {
            const filtered = await loadFreshSeriesData(historyData);
            if (filtered) historyData = filtered;
        }

        if (!historyData || historyData.length === 0) {
            // [I18n] Localized Empty State
            renderEmptyState(target,
                window.locale.t('history.empty.title'),
                window.locale.t('history.empty.desc'),
                () => window.router.navigate('/')
            );
            return;
        }

        // Setup Actions
        window[`MyPageActions_${target.id}`] = {
            deleteHistory: async (seriesId) => {
                // [I18n] Localized Confirm Modal
                const confirmed = await modal.confirm(
                    window.locale.t('history.delete_confirm.msg'),
                    window.locale.t('history.delete_confirm.title')
                );
                if (confirmed) {
                    await HistoryService.removeHistory(user.uid, seriesId);
                    // Local remove
                    historyData = historyData.filter(h => h.seriesId !== seriesId);
                    updateView(currentPage);
                }
            },
            setPage: (p) => {
                updateView(p);
                // Scroll to top of list?
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };

        updateView(1);
    };

    await init();

};
